/* DocumentUpload — /admin/document-upload
 * Presigned-URL bulk upload: Select → Validate → Upload → Complete
 * Files never touch the server — browser uploads directly to S3.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import css from '../AdminPage.module.css';
import {
  useBulkUploadStart,
  useBulkUploadValidate,
  useBulkUploadConfirm,
  useBulkUploadComplete,
  useBulkUploadActive,
  useBulkUploadProgress,
  useBulkUploadRefreshUrls,
  useDocumentCategories,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';

// ── Types ──────────────────────────────────────────

interface FileMeta {
  name: string;
  size: number;
  file: File;
}

interface ValidatedFile {
  id: string;
  name: string;
  originalName: string;
  size: number;
  reference: string | null;
  caseRef: string | null;
  status: string;
  error: string | null;
  s3Key: string | null;
  presignedUrl: string | null;
  contentType: string;
  // upload runtime
  uploadStatus: 'pending' | 'uploading' | 'done' | 'failed';
  uploadProgress: number;
  uploadError?: string;
  // Phase 2: content hash for dedup
  contentHash?: string | null;
  // Phase 2: PDF page count
  pageCount?: number | null;
}

type Stage = 'select' | 'validate' | 'upload' | 'complete';

const MAX_CONCURRENT = 6;
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];
const URL_REFRESH_BUFFER_MS = 10 * 60 * 1000; // refresh URLs 10 min before expiry
const PRESIGN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour (must match backend cfg.PRESIGN_EXPIRY)
const PAGE_SIZE = 50;

// ── Helpers ────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatEta(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

/**
 * Compute a hex SHA-256 digest of a File using the Web Crypto API.
 * Returns null on any error (older browsers or very large files that OOM).
 */
async function sha256Hex(file: File): Promise<string | null> {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/**
 * Count pages in a PDF without external dependencies.
 * Reads up to 500 KB of the file and looks for the Pages dictionary /Count.
 * Returns null on error or for non-PDF files.
 */
async function getPdfPageCount(file: File): Promise<number | null> {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return null;
  try {
    const buf = await file.slice(0, 500_000).arrayBuffer();
    const bytes = new Uint8Array(buf);
    const CHUNK = 8192;
    const chunks: string[] = [];
    for (let i = 0; i < bytes.length; i += CHUNK) {
      chunks.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
    }
    const text = chunks.join('');
    const m = text.match(/\/Type\s*\/Pages[\s\S]{0,300}?\/Count\s+(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  } catch {
    return null;
  }
}

function statusLabel(status: string): { text: string; cls: string } {
  switch (status) {
    case 'validated':
      return { text: 'Ready', cls: css.badgeSuccess };
    case 'no_reference':
      return { text: 'No Reference', cls: css.badgeWarning };
    case 'ref_not_found':
      return { text: 'Ref Not Found', cls: css.badgeWarning };
    case 'no_base_dir':
      return { text: 'No Base Dir', cls: css.badgeWarning };
    case 'invalid_type':
      return { text: 'Invalid Type', cls: css.badgeError };
    default:
      return { text: status, cls: css.badgeNeutral };
  }
}

function uploadStatusBadge(s: ValidatedFile['uploadStatus']): { text: string; cls: string } {
  switch (s) {
    case 'pending':
      return { text: 'Pending', cls: css.badgeNeutral };
    case 'uploading':
      return { text: 'Uploading', cls: css.badgeInfo };
    case 'done':
      return { text: 'Done', cls: css.badgeSuccess };
    case 'failed':
      return { text: 'Failed', cls: css.badgeError };
  }
}

// ── XHR upload with progress ───────────────────────

function uploadFileToS3(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 returned ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));

    signal.addEventListener('abort', () => {
      xhr.abort();
      reject(new Error('Cancelled'));
    });
    xhr.send(file);
  });
}

async function uploadWithRetry(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  let lastErr: Error | null = null;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      await uploadFileToS3(url, file, contentType, onProgress, signal);
      return;
    } catch (err) {
      lastErr = err instanceof Error ? err : new Error(String(err));
      if (signal.aborted) throw lastErr;
      if (attempt < MAX_RETRIES - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
      }
    }
  }
  throw lastErr;
}

// ── Component ──────────────────────────────────────

export default function DocumentUpload() {
  const { showToast } = useToast();

  // Mutations / queries
  const startMut = useBulkUploadStart();
  const validateMut = useBulkUploadValidate();
  const confirmMut = useBulkUploadConfirm();
  const completeMut = useBulkUploadComplete();
  const activeQuery = useBulkUploadActive();
  const refreshUrlsMut = useBulkUploadRefreshUrls();
  const { data: categories = [] } = useDocumentCategories();

  // State
  const [stage, setStage] = useState<Stage>('select');
  const [defaultCategoryId, setDefaultCategoryId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileMeta[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [validatedFiles, setValidatedFiles] = useState<ValidatedFile[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [uploading, setUploading] = useState(false);
  const [completionResult, setCompletionResult] = useState<Record<string, unknown> | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [urlsGeneratedAt, setUrlsGeneratedAt] = useState<string | null>(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState<
    { originalKey: string; renamedTo: string; file: string }[]
  >([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);

  // Poll progress while uploading (for ETA)
  const progressQuery = useBulkUploadProgress(sessionId, uploading);

  // ── Check for active session on mount ──
  useEffect(() => {
    if (activeQuery.data?.active && activeQuery.data.session) {
      const s = activeQuery.data.session;
      setSessionId(String(s.sessionId));
      showToast(`Resuming upload session by ${String(s.createdBy)}`, 'info');
      if (s.status === 'uploading') setStage('upload');
      else setStage('validate');
    }
  }, [activeQuery.data?.active]);

  // ── File selection ──────────────────────

  const addFiles = useCallback((files: FileList | File[]) => {
    const items: FileMeta[] = Array.from(files).map((f) => ({
      name: f.name,
      size: f.size,
      file: f,
    }));
    setSelectedFiles((prev) => [...prev, ...items]);
  }, []);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  // ── Stage 1 → 2: Start + Validate ──────

  async function handleValidate() {
    if (selectedFiles.length === 0) return;
    try {
      const startRes = await startMut.mutateAsync({
        files: selectedFiles.map((f) => ({ name: f.name, size: f.size })),
        defaultCategoryId: defaultCategoryId ?? undefined,
      });
      const sid = startRes.sessionId;
      setSessionId(sid);

      const valRes = await validateMut.mutateAsync(sid);
      setSummary(valRes.summary);
      setUrlsGeneratedAt(valRes.urlsGeneratedAt || new Date().toISOString());
      if (valRes.duplicateWarnings) setDuplicateWarnings(valRes.duplicateWarnings);

      const vFiles: ValidatedFile[] = valRes.files.map((f: Record<string, unknown>) => ({
        id: f.id as string,
        name: f.name as string,
        originalName: f.originalName as string,
        size: f.size as number,
        reference: f.reference as string | null,
        caseRef: f.caseRef as string | null,
        status: f.status as string,
        error: f.error as string | null,
        s3Key: f.s3Key as string | null,
        presignedUrl: f.presignedUrl as string | null,
        contentType: (f.contentType as string) || 'application/octet-stream',
        uploadStatus: 'pending',
        uploadProgress: 0,
      }));
      setValidatedFiles(vFiles);
      setStage('validate');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Validation failed';
      showToast(msg, 'error');
    }
  }

  // ── URL expiry check & refresh ─────────

  async function refreshUrlsIfNeeded(): Promise<void> {
    if (!urlsGeneratedAt || !sessionId) return;
    const generatedAt = new Date(urlsGeneratedAt).getTime();
    const expiresAt = generatedAt + PRESIGN_EXPIRY_MS;
    const now = Date.now();
    if (expiresAt - now > URL_REFRESH_BUFFER_MS) return; // still fresh

    try {
      const pendingIds = validatedFiles
        .filter((f) => f.presignedUrl && f.uploadStatus === 'pending')
        .map((f) => f.id);
      if (pendingIds.length === 0) return;

      const result = await refreshUrlsMut.mutateAsync({ sessionId, fileIds: pendingIds });
      const urlMap = new Map(
        result.refreshed.map((r: { id: string; presignedUrl: string }) => [r.id, r.presignedUrl]),
      );
      setValidatedFiles((prev) =>
        prev.map((f) => (urlMap.has(f.id) ? { ...f, presignedUrl: urlMap.get(f.id)! } : f)),
      );
      setUrlsGeneratedAt(result.urlsGeneratedAt);
      showToast(`Refreshed ${result.refreshed.length} presigned URLs`, 'info');
    } catch (err) {
      console.error('URL refresh failed:', err);
    }
  }

  // ── Stage 2 → 3: Upload ────────────────

  async function handleUpload() {
    setStage('upload');
    setUploading(true);
    abortRef.current = new AbortController();

    // Refresh URLs if they're approaching expiry
    await refreshUrlsIfNeeded();

    const filesToUpload = validatedFiles.filter((f) => f.presignedUrl);
    const fileMap = new Map(selectedFiles.map((f) => [f.name, f]));
    let cursor = 0;
    let filesSinceLastCheck = 0;
    const inFlight = new Set<Promise<void>>();

    const processOne = async (vf: ValidatedFile) => {
      const meta = fileMap.get(vf.originalName) || fileMap.get(vf.name);
      if (!meta || !vf.presignedUrl) return;

      setValidatedFiles((prev) =>
        prev.map((f) =>
          f.id === vf.id ? { ...f, uploadStatus: 'uploading', uploadProgress: 0 } : f,
        ),
      );

      try {
        await uploadWithRetry(
          vf.presignedUrl,
          meta.file,
          vf.contentType,
          (pct) => {
            setValidatedFiles((prev) =>
              prev.map((f) => (f.id === vf.id ? { ...f, uploadProgress: pct } : f)),
            );
          },
          abortRef.current!.signal,
        );
        // Compute SHA-256 for ledger dedup (non-blocking, best-effort)
        const contentHash = await sha256Hex(meta.file);
        // Compute PDF page count (non-blocking, best-effort)
        const pageCount = await getPdfPageCount(meta.file);
        setValidatedFiles((prev) =>
          prev.map((f) =>
            f.id === vf.id
              ? { ...f, uploadStatus: 'done', uploadProgress: 100, contentHash, pageCount }
              : f,
          ),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setValidatedFiles((prev) =>
          prev.map((f) =>
            f.id === vf.id ? { ...f, uploadStatus: 'failed', uploadError: msg } : f,
          ),
        );
      }
      filesSinceLastCheck++;
    };

    // Concurrent upload pool
    while (cursor < filesToUpload.length || inFlight.size > 0) {
      // Periodically check if URLs need refreshing (every 50 files)
      if (filesSinceLastCheck >= 50) {
        filesSinceLastCheck = 0;
        await refreshUrlsIfNeeded();
      }
      while (inFlight.size < MAX_CONCURRENT && cursor < filesToUpload.length) {
        if (abortRef.current?.signal.aborted) break;
        const vf = filesToUpload[cursor++];
        const p = processOne(vf).then(() => {
          inFlight.delete(p);
        });
        inFlight.add(p);
      }
      if (inFlight.size > 0) await Promise.race(inFlight);
    }

    // ── Confirm + Complete ──
    try {
      const finalFiles = await new Promise<ValidatedFile[]>((resolve) => {
        setValidatedFiles((cur) => {
          resolve(cur);
          return cur;
        });
      });

      const results = finalFiles
        .filter((f) => f.presignedUrl)
        .map((f) => ({
          filename: f.name,
          success: f.uploadStatus === 'done',
          error: f.uploadError,
          contentHash: f.contentHash ?? undefined,
          pageCount: f.pageCount ?? undefined,
        }));

      if (sessionId) {
        await confirmMut.mutateAsync({ sessionId, results });
        const completion = await completeMut.mutateAsync(sessionId);
        setCompletionResult(completion);
      }
    } catch (err) {
      console.error('Completion error:', err);
    }

    setUploading(false);
    setStage('complete');
  }

  function handleCancel() {
    abortRef.current?.abort();
    setUploading(false);
    setShowCancelDialog(true);
  }

  async function confirmCancel(deleteFiles: boolean) {
    setShowCancelDialog(false);
    if (!sessionId) return;
    try {
      const { data } = await (
        await import('../../../services/api')
      ).default.post(`/api/admin/upload-documents/cancel/${sessionId}`, { deleteFiles });
      const msg =
        deleteFiles && data.deletedFiles
          ? `Cancelled — ${data.deletedFiles} files deleted from S3`
          : 'Upload cancelled';
      showToast(msg, 'info');
    } catch {
      showToast('Cancel request failed', 'error');
    }
    handleReset();
  }

  function handleReset() {
    setStage('select');
    setSelectedFiles([]);
    setValidatedFiles([]);
    setSummary({});
    setSessionId(null);
    setCompletionResult(null);
    setFilter('all');
    setUrlsGeneratedAt(null);
    setDuplicateWarnings([]);
    setShowCancelDialog(false);
    setTablePage(0);
  }

  // ── Computed values ─────────────────────

  const totalDone = validatedFiles.filter((f) => f.uploadStatus === 'done').length;
  const totalFailed = validatedFiles.filter((f) => f.uploadStatus === 'failed').length;
  const totalToUpload = validatedFiles.filter((f) => f.presignedUrl).length;
  const overallProgress =
    totalToUpload > 0 ? Math.round(((totalDone + totalFailed) / totalToUpload) * 100) : 0;

  const filteredFiles =
    filter === 'all' ? validatedFiles : validatedFiles.filter((f) => f.status === filter);

  const eta = progressQuery.data?.estimatedRemaining;

  function paginate<T>(items: T[]): T[] {
    return items.slice(tablePage * PAGE_SIZE, (tablePage + 1) * PAGE_SIZE);
  }

  function PaginationControls({ total }: { total: number }) {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--gray-500)',
        }}
      >
        <span>
          Showing {tablePage * PAGE_SIZE + 1}–{Math.min((tablePage + 1) * PAGE_SIZE, total)} of{' '}
          {total}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className={css.btnSecondary}
            style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
            disabled={tablePage === 0}
            onClick={() => setTablePage(0)}
          >
            «
          </button>
          <button
            className={css.btnSecondary}
            style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
            disabled={tablePage === 0}
            onClick={() => setTablePage((p) => p - 1)}
          >
            ‹
          </button>
          <span style={{ padding: '2px 8px', lineHeight: '24px' }}>
            Page {tablePage + 1} / {totalPages}
          </span>
          <button
            className={css.btnSecondary}
            style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
            disabled={tablePage >= totalPages - 1}
            onClick={() => setTablePage((p) => p + 1)}
          >
            ›
          </button>
          <button
            className={css.btnSecondary}
            style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
            disabled={tablePage >= totalPages - 1}
            onClick={() => setTablePage(() => totalPages - 1)}
          >
            »
          </button>
        </div>
      </div>
    );
  }

  // ── Active session banner ────────────────

  if (activeQuery.data?.active && !sessionId) {
    const s = activeQuery.data.session;
    const activeSessionId = String(s?.sessionId ?? '');
    const handleForceCancel = async () => {
      if (!activeSessionId) return;
      if (
        !window.confirm(
          'Are you sure you want to force-cancel this upload session? This cannot be undone.',
        )
      )
        return;
      try {
        const api = (await import('../../../services/api')).default;
        await api.post(`/api/admin/upload-documents/cancel/${activeSessionId}`, {
          deleteFiles: false,
        });
        showToast('Session cancelled', 'info');
        activeQuery.refetch();
      } catch {
        showToast('Failed to cancel session', 'error');
      }
    };
    return (
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>📤 Bulk Document Upload</h2>
        </div>
        <div
          style={{
            padding: 24,
            background: 'var(--amber-50)',
            borderRadius: 8,
            border: '1px solid var(--amber-200)',
          }}
        >
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Upload in progress</p>
          <p>
            Started by <strong>{String(s?.createdBy ?? '')}</strong> — {Number(s?.totalFiles ?? 0)}{' '}
            files, {Number(s?.filesSucceeded ?? 0)} succeeded
          </p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: 4 }}>
            Started {s?.startedAt ? new Date(String(s.startedAt)).toLocaleString() : 'unknown time'}
          </p>
          <button className={css.btnDanger} style={{ marginTop: 12 }} onClick={handleForceCancel}>
            Force Cancel Session
          </button>
        </div>
      </div>
    );
  }

  // ── RENDER ──────────────────────────────

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>📤 Bulk Document Upload</h2>
        <p className={css.sectionSubtitle}>
          {stage === 'select' &&
            'Select files to upload — references will be extracted from filenames'}
          {stage === 'validate' && 'Review validation results — then confirm to upload'}
          {stage === 'upload' && 'Uploading files directly to S3...'}
          {stage === 'complete' && 'Upload complete — review results'}
        </p>
      </div>

      {/* ═══ STAGE 1: SELECT FILES ═══ */}
      {stage === 'select' && (
        <>
          <div
            className={`${css.dropZone} ${dragOver ? css.dropZoneActive : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{ marginBottom: 16 }}
          >
            <input
              ref={fileRef}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            <input
              ref={folderRef}
              type="file"
              /* @ts-expect-error webkitdirectory is non-standard */
              webkitdirectory=""
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📎</div>
            <div>Drop files here or click to select</div>
            <div
              style={{ fontSize: 'var(--font-size-xs)', marginTop: 4, color: 'var(--gray-500)' }}
            >
              PDF, DOCX, XLS, MSG, EML, images, archives — all types supported
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className={css.btnSecondary} onClick={() => folderRef.current?.click()}>
              Select Folder
            </button>
          </div>

          {/* Default category picker */}
          {categories.length > 0 && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}
              >
                Default category:
              </label>
              <select
                className={css.formSelect}
                value={defaultCategoryId ?? ''}
                onChange={(e) =>
                  setDefaultCategoryId(e.target.value ? Number(e.target.value) : null)
                }
                style={{ maxWidth: 220 }}
              >
                <option value="">None (uncategorised)</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                  {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected (
                  {formatSize(selectedFiles.reduce((s, f) => s + f.size, 0))} total)
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className={css.btnPrimary}
                    onClick={handleValidate}
                    disabled={startMut.isPending || validateMut.isPending}
                  >
                    {startMut.isPending || validateMut.isPending
                      ? 'Validating...'
                      : 'Validate & Continue'}
                  </button>
                  <button className={css.btnSecondary} onClick={() => setSelectedFiles([])}>
                    Clear
                  </button>
                </div>
              </div>

              <table className={css.table}>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Size</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(selectedFiles).map((f, i) => (
                    <tr key={tablePage * PAGE_SIZE + i}>
                      <td style={{ fontWeight: 500 }}>{f.name}</td>
                      <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                        {formatSize(f.size)}
                      </td>
                      <td>
                        <button
                          className={`${css.btnSecondary} ${css.btnSmall}`}
                          onClick={() =>
                            setSelectedFiles((p) =>
                              p.filter((_, j) => j !== tablePage * PAGE_SIZE + i),
                            )
                          }
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls total={selectedFiles.length} />
            </>
          )}

          {selectedFiles.length === 0 && (
            <div className={css.emptyState}>
              <div className={css.emptyIcon}>📂</div>
              <p className={css.emptyMessage}>
                No files selected. Drag and drop, click the zone, or use "Select Folder".
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ STAGE 2: VALIDATION RESULTS (DRY-RUN) ═══ */}
      {stage === 'validate' && (
        <>
          {/* Duplicate warnings */}
          {duplicateWarnings.length > 0 && (
            <div
              style={{
                padding: '12px 16px',
                marginBottom: 16,
                borderRadius: 8,
                background: 'var(--amber-50)',
                border: '1px solid var(--amber-200)',
              }}
            >
              <p style={{ fontWeight: 600, marginBottom: 4 }}>
                {duplicateWarnings.length} file{duplicateWarnings.length !== 1 ? 's' : ''} already
                exist in S3 — renamed to avoid overwriting
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  fontSize: 'var(--font-size-xs)',
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                {duplicateWarnings.slice(0, 20).map((w, i) => (
                  <li key={i}>
                    <code>{w.file}</code>
                  </li>
                ))}
                {duplicateWarnings.length > 20 && (
                  <li>...and {duplicateWarnings.length - 20} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              marginBottom: 20,
            }}
          >
            {[
              { label: 'Ready', key: 'validated', color: 'var(--green-600)' },
              { label: 'No Reference', key: 'no_reference', color: 'var(--amber-600)' },
              { label: 'Not in DB', key: 'ref_not_found', color: 'var(--amber-600)' },
              { label: 'No Base Dir', key: 'no_base_dir', color: 'var(--amber-600)' },
              { label: 'Invalid Type', key: 'invalid_type', color: 'var(--red-600)' },
            ].map((c) => (
              <div
                key={c.key}
                onClick={() => {
                  setFilter(c.key);
                  setTablePage(0);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  background: 'var(--surface-primary)',
                  border: `1px solid ${filter === c.key ? c.color : 'var(--border-primary)'}`,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>
                  {summary[c.key] || 0}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                  {c.label}
                </div>
              </div>
            ))}
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              'all',
              'validated',
              'no_reference',
              'ref_not_found',
              'no_base_dir',
              'invalid_type',
            ].map((f) => (
              <button
                key={f}
                className={filter === f ? css.btnPrimary : css.btnSecondary}
                style={{ padding: '4px 10px', fontSize: 'var(--font-size-xs)' }}
                onClick={() => {
                  setFilter(f);
                  setTablePage(0);
                }}
              >
                {f === 'all' ? 'All' : f === 'validated' ? 'Ready' : f.replace(/_/g, ' ')}
              </button>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
              {filteredFiles.length} files shown
              {summary.validated
                ? ` — ${summary.validated} matched, ${validatedFiles.length - (summary.validated || 0)} unmatched (will go to staging)`
                : ''}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className={css.btnPrimary} onClick={handleUpload}>
                Upload All {validatedFiles.length} Files
              </button>
              <button className={css.btnSecondary} onClick={handleReset}>
                Cancel
              </button>
            </div>
          </div>

          <table className={css.table}>
            <thead>
              <tr>
                <th>File</th>
                <th>Reference</th>
                <th>Case Ref</th>
                <th>Size</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginate(filteredFiles).map((f) => {
                const badge = statusLabel(f.status);
                return (
                  <tr key={f.id}>
                    <td
                      style={{
                        fontWeight: 500,
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={f.originalName}
                    >
                      {f.name}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                      {f.reference || '—'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                      {f.caseRef || '—'}
                    </td>
                    <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                      {formatSize(f.size)}
                    </td>
                    <td>
                      <span className={badge.cls} title={f.error || undefined}>
                        {badge.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationControls total={filteredFiles.length} />
        </>
      )}

      {/* ═══ STAGE 3: UPLOADING ═══ */}
      {stage === 'upload' && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontWeight: 600 }}>
                {totalDone + totalFailed} of {totalToUpload} uploaded
                {totalFailed > 0 && (
                  <span style={{ color: 'var(--red-600)', marginLeft: 8 }}>
                    ({totalFailed} failed)
                  </span>
                )}
              </span>
              <span style={{ color: 'var(--gray-500)' }}>
                {overallProgress}%{eta ? ` — ETA: ${formatEta(eta)}` : ''}
              </span>
            </div>
            <div className={css.progressBar}>
              <div
                className={css.progressFill}
                style={{ width: `${overallProgress}%`, transition: 'width 0.3s' }}
              />
            </div>
          </div>

          {uploading && (
            <div style={{ marginBottom: 16 }}>
              <button className={css.btnDanger} onClick={handleCancel}>
                Cancel Upload
              </button>
            </div>
          )}

          {/* Cancel confirmation dialog */}
          {showCancelDialog && (
            <div
              style={{
                padding: 20,
                borderRadius: 8,
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-primary)',
                marginBottom: 16,
              }}
            >
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Upload cancelled</p>
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  marginBottom: 12,
                }}
              >
                {totalDone} file{totalDone !== 1 ? 's' : ''} already uploaded to S3. What would you
                like to do?
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={css.btnSecondary} onClick={() => confirmCancel(false)}>
                  Keep uploaded files
                </button>
                {totalDone > 0 && (
                  <button className={css.btnDanger} onClick={() => confirmCancel(true)}>
                    Delete uploaded files from S3
                  </button>
                )}
              </div>
            </div>
          )}

          <table className={css.table}>
            <thead>
              <tr>
                <th>File</th>
                <th>Reference</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginate(validatedFiles.filter((f) => f.presignedUrl)).map((f) => {
                const badge = uploadStatusBadge(f.uploadStatus);
                return (
                  <tr key={f.id}>
                    <td
                      style={{
                        fontWeight: 500,
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={f.name}
                    >
                      {f.name}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                      {f.reference || '—'}
                    </td>
                    <td style={{ width: 120 }}>
                      {f.uploadStatus === 'uploading' && (
                        <div className={css.progressBar} style={{ height: 6 }}>
                          <div
                            className={css.progressFill}
                            style={{ width: `${f.uploadProgress}%` }}
                          />
                        </div>
                      )}
                      {f.uploadStatus === 'done' && (
                        <span
                          style={{ color: 'var(--green-600)', fontSize: 'var(--font-size-xs)' }}
                        >
                          100%
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={badge.cls} title={f.uploadError}>
                        {badge.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationControls total={validatedFiles.filter((f) => f.presignedUrl).length} />
        </>
      )}

      {/* ═══ STAGE 4: COMPLETE ═══ */}
      {stage === 'complete' && (
        <>
          <div
            style={{
              padding: 24,
              borderRadius: 8,
              background: 'var(--green-50)',
              border: '1px solid var(--green-200)',
              marginBottom: 20,
            }}
          >
            <p style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Upload Complete</p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 12,
              }}
            >
              <div>
                <span style={{ fontWeight: 600 }}>
                  {Number(completionResult?.succeeded ?? totalDone)}
                </span>{' '}
                uploaded
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--red-600)' }}>
                  {Number(completionResult?.uploadFailed ?? totalFailed)}
                </span>{' '}
                failed
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--amber-600)' }}>
                  {Number(completionResult?.unmatched ?? 0)}
                </span>{' '}
                unmatched
              </div>
              {!!completionResult?.elapsedSeconds && (
                <div>
                  Duration:{' '}
                  <span style={{ fontWeight: 600 }}>
                    {formatEta(Number(completionResult.elapsedSeconds))}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {!!completionResult?.reportUrl && (
              <a
                href={String(completionResult.reportUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className={css.btnPrimary}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                Download Report
              </a>
            )}
            {!!completionResult?.unmatchedZipUrl && (
              <a
                href={String(completionResult.unmatchedZipUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className={css.btnSecondary}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                Download Unmatched Files ({Number(completionResult.unmatched ?? 0)})
              </a>
            )}
            <button className={css.btnSecondary} onClick={handleReset}>
              Upload More
            </button>
          </div>

          {/* Final file table */}
          <table className={css.table}>
            <thead>
              <tr>
                <th>File</th>
                <th>Reference</th>
                <th>Validation</th>
                <th>Upload</th>
              </tr>
            </thead>
            <tbody>
              {paginate(validatedFiles).map((f) => {
                const vBadge = statusLabel(f.status);
                const uBadge = f.presignedUrl ? uploadStatusBadge(f.uploadStatus) : null;
                return (
                  <tr key={f.id}>
                    <td
                      style={{
                        fontWeight: 500,
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.name}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                      {f.reference || '—'}
                    </td>
                    <td>
                      <span className={vBadge.cls}>{vBadge.text}</span>
                    </td>
                    <td>
                      {uBadge ? (
                        <span className={uBadge.cls} title={f.uploadError}>
                          {uBadge.text}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationControls total={validatedFiles.length} />
        </>
      )}
    </div>
  );
}
